import { decryptFile, encryptFile, type ManifestFile } from "@timecapsule/crypto";
import { api } from "./api";
import { msg } from "./i18n";

/**
 * Files in capsules (PLAN.md 4.9, 4.11 H10), browser side. A chosen file becomes `bytes` ready
 * to encrypt: images are downscaled and re-encoded (which also drops EXIF, GPS included), other
 * files are taken as they are. Nothing here decides what is safe to display; Attachments.tsx does.
 */
export type Prepared = { name: string; type: string; bytes: Uint8Array; shrunk: boolean };

/** Longest side after downscaling (PLAN.md 4.9). */
const MAX_SIDE = 1600;
const QUALITIES = [0.8, 0.7, 0.6, 0.5, 0.4];
/** Re-encoded; GIF keeps its animation and is sent as is. */
const RE_ENCODE = new Set(["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/avif", "image/bmp"]);

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

const withExtension = (name: string, ext: string) => (name.replace(/\.[^.]{1,10}$/, "") || "image") + ext;

let webpSupport: Promise<boolean> | null = null;

/** Whether this browser can encode WebP from a canvas (Safari before 17 cannot). */
function canEncodeWebp(): Promise<boolean> {
  webpSupport ??= canvasBlob(Object.assign(document.createElement("canvas"), { width: 1, height: 1 }), "image/webp", 0.8).then((b) => b?.type === "image/webp");
  return webpSupport;
}

/**
 * Downscales an image to MAX_SIDE and lowers quality (then size) until it fits `budget` bytes.
 * WebP where the browser can encode it (keeps transparency), JPEG on a white background otherwise.
 */
async function shrinkImage(file: File, budget: number): Promise<Prepared> {
  const type = (await canEncodeWebp()) ? "image/webp" : "image/jpeg";
  const bitmap = await createImageBitmap(file);
  try {
    let scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    for (;;) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error(msg().attachments.noImages);
      if (type === "image/jpeg") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      for (const quality of QUALITIES) {
        const blob = await canvasBlob(canvas, type, quality);
        if (blob && blob.size <= budget) {
          return { name: withExtension(file.name, type === "image/webp" ? ".webp" : ".jpg"), type, bytes: new Uint8Array(await blob.arrayBuffer()), shrunk: true };
        }
      }
      scale *= 0.75;
      if (bitmap.width * scale < 200 && bitmap.height * scale < 200) throw new Error(`${file.name} does not fit even when made small`);
    }
  } finally {
    bitmap.close();
  }
}

/** Makes a chosen file ready to encrypt, within `budget` bytes (what is left of the capsule's allowance). */
export async function prepareFile(file: File, budget: number): Promise<Prepared> {
  if (RE_ENCODE.has(file.type)) {
    try {
      return await shrinkImage(file, budget);
    } catch (err) {
      // A format this browser cannot decode (e.g. HEIC outside Safari): send it as is if it fits.
      if (file.size > budget) throw err instanceof Error ? err : new Error(String(err));
    }
  }
  if (file.size > budget) throw new Error(msg().attachments.tooBig(file.name, formatBytes(file.size), formatBytes(Math.max(0, budget))));
  return { name: file.name, type: file.type || "application/octet-stream", bytes: new Uint8Array(await file.arrayBuffer()), shrunk: false };
}

/** Encrypts and uploads one file; the returned entry goes into the manifest. Safe to repeat. */
export async function uploadFile(p: Prepared): Promise<ManifestFile> {
  const { blob, entry } = await encryptFile(p.bytes, { name: p.name, type: p.type });
  await api.uploadBlob(entry.sha256, blob);
  return entry;
}

/** Downloads a blob and decrypts it; the SHA-256 is checked before decryption. */
export async function fetchFile(entry: ManifestFile): Promise<Uint8Array> {
  return decryptFile(await api.fetchBlob(entry.sha256), entry);
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KiB`;
  return `${(n / 1024 / 1024).toFixed(1)} MiB`;
}
